// ArrivalHeader.tsx — the redesigned TOP of Today (NEW_UI). It is a HEADER only:
// the real self-advancing course feed renders below it, untouched.
//
//   PHASE 1  the arrival question IS the first view. No notifications here. Re-asked
//            on every fresh open + resume; the chosen state then LOCKS IN for the
//            session (survives leaving Today and coming back).
//   PHASE 2  the question collapses; the header reconstructs to that arrival's
//            matched action (white "title box", no crisis card), with a notification
//            slot + a Calm / Rebuild / Hold / Toolkit menu.
//
// Tiles carry an abstract concentric-ring motif blended faintly into the background
// (premium, partly off-edge) rather than literal icons. No em-dashes in any copy.
import React from 'react';
import { App as CapApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { Icon, type IconName } from '@/components/icons';
import { useAuth } from '@/hooks/useAuth';
import { FOCUSED_ARRIVAL, TODAY_ONE_JOB } from '@/config/features';
import {
  C, SP, R, F, SIZE, W, eyebrow,
  ARRIVALS, VARIANTS, MENU,
  type ArrivalId, type ActionKey, type MotifKind,
} from './theme';

export interface ArrivalHeaderProps {
  onOpenCrisis: () => void;
  onSettle: () => void;      // emotions list, then approach choice (Calm Your Body)
  // TODAY_ONE_JOB: the arrival state PRE-ANSWERS the Calm question. The matched
  // box's settle action starts the default 2-minute settle directly instead of
  // opening the emotions chooser (the "Calm your body" action row still opens the
  // full chooser for anyone who wants to pick).
  onSettleDirect?: () => void;
  onRelax: () => void;       // relaxation breathing
  onSettleNow: () => void;   // full-width shortcut: straight into a 2-minute breathing reset
  onHold: () => void;        // Boundary & Urge tools page
  onContinue: () => void;    // the programme feed below (Rebuild)
  onContinueProgramme: () => void; // rebuild mode + open the last-visited programme's next lesson
  onSelfCompassion: () => void; // switch to the self-compassion course
  onOpenToolkit: () => void;
  onOpenLibrary: () => void;
  // Dev-only (PIN-locked dev mode): a small "Ask ReNurture" button under the actions.
  showAsk?: boolean;
  onOpenAsk?: () => void;
  // "Waiting on you" list (phase 2): flagged actions the app is holding for the user.
  checkinDue: boolean;
  onStartCheckin: () => void;
  remindersSet: boolean;
  onSetupReminders: () => void;
  careDue: boolean;             // the CARE grounding prompt after a trauma trigger
  onOpenCare: () => void;
  onDismissCare: () => void;
  // true while the user has never run a breathing session — invites a first try
  // (routes into the relaxation time picker). Clears itself once they've had one.
  breathingNew?: boolean;
  // Sub-label under the universal "Continue your programme" action (the next lesson).
  continueSub?: string;
  // true once the user has real programme progress — switches the "work on
  // myself" header from a neutral first-use invite to a warmer "carry on".
  hasStarted: boolean;
  // lets the host dim the programme feed until a state is chosen. Fires on mount AND
  // whenever the arrival becomes active/inactive — so hosts must NOT treat it as "the
  // user just changed their arrival" (it also fires when Today remounts). For that, use
  // onArrivalPicked below.
  onArrivedChange?: (arrived: boolean) => void;
  // Fires ONLY when the user actively taps an arrival tile — never on mount/remount.
  // The host uses this to re-hide the programme feed for a fresh arrival, without the
  // mount-time onArrivedChange falsely re-hiding it on every remount (e.g. after a
  // programme switch, which remounts Today).
  onArrivalPicked?: () => void;
  // Activation card (SHOW_PROGRAMME_CTA): a prominent one-tap route into the current
  // lesson, shown at the very top of the arrival screen so the programme is never
  // buried. Null hides it (flag off, or on the self-compassion route).
  programmeCta?: { title: string; sub: string; onOpen: () => void } | null;
  // TODAY_ONE_JOB: extra "When you're ready" cards the host is holding (the deferred
  // act survey and chapter feedback, moved out of the post-lesson chain). They join
  // the same one-at-a-time rotation as the built-in cards.
  waitingExtras?: Array<{ key: string; icon: IconName; title: string; sub: string; onTap: () => void; onDismiss?: () => void }>;
}

export function greeting(name?: string | null): string {
  const h = new Date().getHours();
  const base = h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening';
  // Personalise with the first name / nickname collected at sign-up; legacy accounts
  // (no name) fall back to the plain greeting.
  const who = name && name.trim() ? `, ${name.trim()}` : '';
  return `${base}${who}.`;
}

// Abstract, distinct-per-tile motifs. Thin line work, blended faintly into the tile
// and bleeding off the edge. Each says something about the state without being a
// literal icon: an alert mark, a destabilised core, two separate bodies, an
// out-of-rhythm line, balanced stones, a ball on a smooth curve, a sunrise, a
// shield, branching paths.
function Motif({ kind, color, size }: { kind: MotifKind; color: string; size: number }) {
  const p = { stroke: color, strokeWidth: 1.7, fill: 'none', strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };
  return (
    <svg width={size} height={size} viewBox="0 0 160 160" fill="none" aria-hidden>
      {kind === 'alert' && (<>
        <path d="M80 38 L130 122 H30 Z" {...p} />
        <line x1="80" y1="68" x2="80" y2="96" {...p} />
        <circle cx="80" cy="108" r="2.6" fill={color} />
      </>)}
      {kind === 'disturb' && (<>
        <circle cx="80" cy="84" r="30" {...p} />
        <circle cx="67" cy="84" r="30" {...p} opacity={0.5} />
        <circle cx="93" cy="84" r="30" {...p} opacity={0.5} />
      </>)}
      {kind === 'separate' && (<>
        <circle cx="50" cy="84" r="26" {...p} />
        <circle cx="116" cy="84" r="26" {...p} />
      </>)}
      {kind === 'irregular' && (
        <polyline points="16,92 42,70 60,112 86,56 108,116 134,74 152,98" {...p} />
      )}
      {kind === 'balance' && (<>
        <ellipse cx="80" cy="122" rx="34" ry="12" {...p} />
        <ellipse cx="78" cy="100" rx="22" ry="10" {...p} />
        <ellipse cx="84" cy="80" rx="27" ry="10" {...p} />
        <ellipse cx="80" cy="60" rx="15" ry="8" {...p} />
      </>)}
      {kind === 'journey' && (<>
        <path d="M18 124 Q80 36 142 124" {...p} />
        <circle cx="106" cy="90" r="9" {...p} />
      </>)}
      {kind === 'horizon' && (<>
        <line x1="20" y1="106" x2="140" y2="106" {...p} />
        <path d="M58 106 A22 22 0 0 1 102 106" {...p} />
        <line x1="80" y1="64" x2="80" y2="56" {...p} />
        <line x1="52" y1="74" x2="47" y2="68" {...p} />
        <line x1="108" y1="74" x2="113" y2="68" {...p} />
      </>)}
      {kind === 'hold' && (
        <path d="M80 40 L118 54 V86 C118 110 100 124 80 132 C60 124 42 110 42 86 V54 Z" {...p} />
      )}
      {kind === 'paths' && (<>
        <path d="M80 134 V98" {...p} />
        <path d="M80 98 C80 76 50 76 42 54" {...p} />
        <path d="M80 98 V52" {...p} />
        <path d="M80 98 C80 76 110 76 118 54" {...p} />
        <circle cx="42" cy="50" r="3.5" {...p} />
        <circle cx="80" cy="48" r="3.5" {...p} />
        <circle cx="118" cy="50" r="3.5" {...p} />
      </>)}
    </svg>
  );
}

// A single uniform tile: same size + fonts everywhere, with its motif blended in.
// `big` gives the phase-1 arrival tiles extra room (it's the only content there).
// A white sheen over the tone + a layered warm shadow + a lifted edge give it depth;
// `primary` (the Continue tile) gets a gold chip + hairline so it out-ranks the rest.
// TRIAL_FULL_LOCK: a locked item stays tappable but reads as unavailable.
export const LOCKED_LOOK: React.CSSProperties = { filter: 'saturate(0.35)', opacity: 0.5 };
export function LockBadge() {
  return (
    <span aria-hidden data-lock-icon style={{ position: 'absolute', top: 10, right: 12, display: 'flex', color: 'rgba(60,50,30,0.7)' }}>
      <Icon name="lock" size={16} sw={2} />
    </span>
  );
}

export function Tile({ tone, ink, title, sub, motif, icon, full, big, primary, image, imageOpacity = 0.4, imagePosition = 'center', badge, locked, onClick }: {
  tone: string; ink: string; title: string; sub: string; motif: MotifKind; icon?: IconName; full?: boolean; big?: boolean; primary?: boolean; image?: string; imageOpacity?: number; imagePosition?: string; badge?: React.ReactNode; locked?: boolean; onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      aria-label={locked ? `${title}, locked, for members` : undefined}
      style={{
        ...(locked ? LOCKED_LOOK : null),
        position: 'relative', overflow: 'hidden', textAlign: 'left', border: 'none', cursor: 'pointer',
        background: `linear-gradient(158deg, rgba(255,255,255,0.4), rgba(255,255,255,0) 62%), ${tone}`,
        color: ink, borderRadius: 20, padding: big ? '17px 17px' : '14px 15px',
        minHeight: full ? 74 : (big ? 118 : 96),
        gridColumn: full ? '1 / -1' : undefined,
        display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', gap: 4,
        boxShadow: primary ? `${SH_LIFT}, ${HI}, inset 0 0 0 1px rgba(201,155,29,0.32)` : `${SH}, ${HI}`,
      }}
    >
      {image ? (
        // Hand-painted overlay covering the tile, faded at the bottom so the title stays
        // legible over it (in place of the abstract line motif).
        <img src={image} alt="" aria-hidden decoding="async" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: imagePosition, opacity: imageOpacity, pointerEvents: 'none', WebkitMaskImage: 'linear-gradient(to bottom, #000 0%, rgba(0,0,0,0.35) 78%, transparent 100%)', maskImage: 'linear-gradient(to bottom, #000 0%, rgba(0,0,0,0.35) 78%, transparent 100%)' }} />
      ) : (
        <span aria-hidden style={{ position: 'absolute', right: -28, bottom: -34, color: ink, opacity: 0.2, pointerEvents: 'none', lineHeight: 0 }}>
          <Motif kind={motif} color={ink} size={132} />
        </span>
      )}
      {badge}
      {icon && (
        <span aria-hidden style={{ position: 'relative', width: 34, height: 34, borderRadius: 11, background: primary ? 'linear-gradient(150deg,#f0cd6f,#d9a52f)' : 'rgba(255,255,255,0.6)', color: primary ? '#fff' : ink, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 3, boxShadow: primary ? 'inset 0 1px 0 rgba(255,255,255,0.55), 0 3px 8px rgba(160,120,20,0.4)' : CHIP_SH }}>
          <Icon name={icon} size={17} />
        </span>
      )}
      {locked && <LockBadge />}
      <span style={{ position: 'relative', fontFamily: F.title, fontSize: SIZE.h3, fontWeight: W.semibold, lineHeight: 1.15, letterSpacing: '-0.2px' }}>{title}</span>
      <span style={{ position: 'relative', fontFamily: F.ui, fontSize: SIZE.caption, opacity: 0.78 }}>{sub}</span>
    </button>
  );
}

// ── header notification: dismiss hides it for today; returns next day if still
//    outstanding. (Phase 2 only, so page 1 stays a clean selector.) ─────────────
const today = () => new Date().toISOString().slice(0, 10);
export const dismissedToday = (key: string) => {
  try { return localStorage.getItem('renurture.hnotif.' + key) === today(); } catch { return false; }
};
export const dismissForToday = (key: string) => {
  try { localStorage.setItem('renurture.hnotif.' + key, today()); } catch { /* ignore */ }
};

function NotificationCard(p: ArrivalHeaderProps & { bump: () => void }) {
  // `sticky` = this card cannot be dismissed or hidden on tap. The check-in is the
  // only sticky one: it stands until the server holds a submission (props.checkinDue
  // is the backend-verified gate), so opening it and abandoning it half way leaves it
  // exactly where it was. Same rule as the "Waiting on you" list below — this is the
  // FOCUSED_ARRIVAL=false rollback path, and it must not resurrect the old behaviour
  // where a tap or an × lost the wave for the rest of the day.
  let notif: { key: string; title: string; sub: string; icon: IconName; onTap: () => void; sticky?: boolean } | null = null;
  if (p.checkinDue) {
    notif = { key: 'checkin', title: 'Complete a check-in', sub: 'A short, private wellbeing check-in', icon: 'heart', onTap: p.onStartCheckin, sticky: true };
  } else if (!p.remindersSet && !dismissedToday('reminders') && Capacitor.isNativePlatform()) {
    // Reminders are native-only (local notifications); on web they never fire, so
    // don't prompt web users to set one up.
    notif = { key: 'reminders', title: 'Set up a daily reminder', sub: 'A gentle nudge to return each day', icon: 'clock', onTap: p.onSetupReminders };
  }
  if (!notif) return null;
  const n = notif;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: SP.md, background: C.surface, borderRadius: R.lg, padding: '12px 12px 12px 14px', boxShadow: '0 4px 16px rgba(0,0,0,0.06)' }}>
      <span style={{ width: 34, height: 34, borderRadius: R.md, background: 'rgba(63,167,163,0.12)', color: C.brandDark, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Icon name={n.icon} size={18} /></span>
      <button onClick={() => { n.onTap(); if (!n.sticky) { dismissForToday(n.key); p.bump(); } }} style={{ flex: 1, textAlign: 'left', border: 'none', background: 'none', cursor: 'pointer', padding: 0 }}>
        <span style={{ display: 'block', fontFamily: F.title, fontSize: SIZE.h3, fontWeight: W.medium, color: C.text }}>{n.title}</span>
        <span style={{ display: 'block', fontFamily: F.ui, fontSize: SIZE.caption, color: C.textMuted, marginTop: 1 }}>{n.sub}</span>
      </button>
      {!n.sticky && (
        <button aria-label="Dismiss" onClick={() => { dismissForToday(n.key); p.bump(); }} style={{ border: 'none', background: 'none', cursor: 'pointer', color: C.textFaint, fontSize: 18, lineHeight: 1, padding: '4px 6px', flexShrink: 0 }}>×</button>
      )}
    </div>
  );
}

// The chosen arrival LOCKS IN for this app session (survives HomeScreen remounts).
// Cleared only on a fresh open: a cold start (module reloads to null) or a return
// from background/resume (the listener below). Internal navigation never resets it.
const arrivalDayKey = () => new Date().toDateString();
// Persist the chosen arrival in sessionStorage so a tab RELOAD — including Chrome
// discarding a backgrounded tab and restoring it on return — doesn't re-ask "how
// are you arriving?" on the same day (the module vars below are wiped by a reload).
// sessionStorage survives reload/discard but is cleared when the tab is truly
// closed, so a real new session still re-asks — which is the intended behaviour.
const ARRIVAL_KEY = 'rn_arrival';
function loadArrival(): { id: ArrivalId | null; day: string | null } {
  try {
    const raw = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem(ARRIVAL_KEY) : null;
    if (raw) {
      const o = JSON.parse(raw) as { id: ArrivalId; day: string };
      if (o && o.day === arrivalDayKey()) return { id: o.id, day: o.day };
    }
  } catch { /* storage blocked → fall back to in-memory only */ }
  return { id: null, day: null };
}
function saveArrival(id: ArrivalId | null, day: string | null) {
  try {
    if (typeof sessionStorage === 'undefined') return;
    if (id && day) sessionStorage.setItem(ARRIVAL_KEY, JSON.stringify({ id, day }));
    else sessionStorage.removeItem(ARRIVAL_KEY);
  } catch { /* ignore */ }
}
const _arrivalInit = loadArrival();
let sessionArrival: ArrivalId | null = _arrivalInit.id;
let sessionArrivalDay: string | null = _arrivalInit.day; // local calendar day the state was chosen

// "Rebuild" mode: the user chose "Continue your programme" rather than an arrival state.
// It LOCKS IN like an arrival (same day-key lifecycle) so that leaving a lesson and
// returning to Today lands back in the rebuild/programme view — NOT the "how are you
// arriving?" question every time. Stored in sessionStorage so a tab reload keeps it.
const REBUILD_KEY = 'rn_rebuild';
function loadRebuild(): { on: boolean; day: string | null } {
  try {
    const raw = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem(REBUILD_KEY) : null;
    if (raw) { const o = JSON.parse(raw) as { day: string }; if (o && o.day === arrivalDayKey()) return { on: true, day: o.day }; }
  } catch { /* storage blocked */ }
  return { on: false, day: null };
}
function saveRebuild(on: boolean) {
  try {
    if (typeof sessionStorage === 'undefined') return;
    if (on) sessionStorage.setItem(REBUILD_KEY, JSON.stringify({ day: arrivalDayKey() }));
    else sessionStorage.removeItem(REBUILD_KEY);
  } catch { /* ignore */ }
}
const _rebuildInit = loadRebuild();
let sessionRebuild: boolean = _rebuildInit.on;
let sessionRebuildDay: string | null = _rebuildInit.day;

// ── Depth kit — layered warm shadows + a lifted-glass top highlight give surfaces
//    material weight instead of reading as flat 1980s boxes. Shared by every card. ──
export const SH = '0 1px 2px rgba(74,52,20,0.05), 0 10px 30px -14px rgba(74,52,20,0.28)';
export const SH_LIFT = '0 2px 4px rgba(74,52,20,0.06), 0 18px 40px -18px rgba(74,52,20,0.34)';
export const HI = 'inset 0 1px 0 rgba(255,255,255,0.55)';
const CHIP_SH = 'inset 0 1px 0 rgba(255,255,255,0.7), 0 1px 2px rgba(74,52,20,0.16)';
// Faint paper grain + a warm gold glow top-left, layered into the band background so
// the cream ground has texture and atmosphere (no DOM overlay, no stacking issues).
const GRAIN = "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='140' height='140'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/%3E%3CfeColorMatrix type='saturate' values='0'/%3E%3C/filter%3E%3Crect width='140' height='140' filter='url(%23n)' opacity='0.05'/%3E%3C/svg%3E\")";
const GLOW = 'radial-gradient(60% 55% at 22% 12%, rgba(230,179,37,0.10), transparent 60%)';

// Hand-painted per-arrival tile overlays (phase 1). Add an entry as each image lands;
// tiles without one fall back to the abstract line motif.
export const ARRIVAL_IMG: Partial<Record<ArrivalId, string>> = {
  unsafe: '/newbuttonimages/triage_feeling-unsafe-overlay.png',
  ex: '/newbuttonimages/today_steadyyourselfbox.png',
  triggered: '/newbuttonimages/today_calm-your-body-first-overlay.webp',
  hardweek: '/newbuttonimages/today_permission-to-do-less-overlay.png',
};

// Full-bleed band so the reduced-app off-white background spans the screen width and
// lifts to the very top, sitting above the warm feed below. No safe-top here: the
// AppShell header already clears the notch (adding it again was the wasted space).
export const band: React.CSSProperties = {
  background: `${GRAIN}, ${GLOW}, ${C.page}`,
  marginTop: -16,
  marginLeft: 'calc(-1 * (var(--screen-px) + var(--safe-left)))',
  marginRight: 'calc(-1 * (var(--screen-px) + var(--safe-right)))',
  paddingTop: 10,
  paddingBottom: 20,
  paddingLeft: 'calc(var(--screen-px) + var(--safe-left))',
  paddingRight: 'calc(var(--screen-px) + var(--safe-right))',
};

// The activation card: a warm, programme-toned one-tap route straight into the
// current lesson. Rendered at the top of BOTH arrival phases so the course is always
// one obvious tap away, never buried behind the arrival question + menu.
function ProgrammeCtaCard({ cta }: { cta: { title: string; sub: string; onOpen: () => void } }) {
  return (
    <button
      onClick={cta.onOpen}
      style={{
        display: 'flex', alignItems: 'center', gap: 12, width: '100%',
        textAlign: 'left', border: 'none', cursor: 'pointer',
        background: '#f1d9b4', color: '#5a4218', borderRadius: R.lg,
        padding: '14px 16px', marginBottom: 18,
        boxShadow: '0 6px 18px rgba(90,66,24,0.14)',
      }}
    >
      <span style={{ width: 40, height: 40, borderRadius: R.md, background: 'rgba(90,66,24,0.14)', color: '#5a4218', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Icon name="sparkle" size={20} />
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontFamily: F.title, fontSize: SIZE.h3, fontWeight: W.semibold, lineHeight: 1.2 }}>{cta.title}</span>
        <span style={{ display: 'block', fontFamily: F.ui, fontSize: SIZE.caption, opacity: 0.82, marginTop: 2 }}>{cta.sub}</span>
      </span>
      <Icon name="chevRight" size={18} />
    </button>
  );
}

// A universal full-width action row (Continue programme / Calm your body / Your toolkit).
// Gradient fill + layered warm shadow + a lifted-glass edge give it material weight; a
// faint motif bleeds off the corner. `primary` gets a gold chip + hairline so the main
// action out-ranks the others.
function ActionRow({ bg, ink, title, sub, icon, motif, image, imageOpacity = 0.6, onClick, primary, chipBg, chipInk }: {
  bg: string; ink: string; title: string; sub?: string; icon: IconName; motif: MotifKind; image?: string; imageOpacity?: number; onClick: () => void;
  primary?: boolean; chipBg?: string; chipInk?: string;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        position: 'relative', overflow: 'hidden',
        display: 'flex', alignItems: 'center', gap: 15, width: '100%', textAlign: 'left',
        border: 'none', cursor: 'pointer', background: bg, color: ink, borderRadius: 20,
        padding: '16px 16px',
        boxShadow: primary ? `${SH_LIFT}, ${HI}, inset 0 0 0 1px rgba(201,155,29,0.30)` : `${SH}, ${HI}`,
      }}
    >
      {image ? (
        // Hand-painted band across the button (its colour sits to the right, in the space
        // the chevron used to occupy) instead of the abstract line motif.
        <img src={image} alt="" aria-hidden decoding="async" style={{ position: 'absolute', left: 0, right: 0, top: '50%', transform: 'translateY(-50%)', width: '100%', height: 'auto', opacity: imageOpacity, pointerEvents: 'none', WebkitMaskImage: 'linear-gradient(to right, transparent 0%, rgba(0,0,0,0.5) 34%, #000 62%)', maskImage: 'linear-gradient(to right, transparent 0%, rgba(0,0,0,0.5) 34%, #000 62%)' }} />
      ) : (
        <span aria-hidden style={{ position: 'absolute', right: -26, bottom: -30, color: ink, opacity: 0.4, pointerEvents: 'none', lineHeight: 0 }}>
          <Motif kind={motif} color={ink} size={128} />
        </span>
      )}
      <span style={{ position: 'relative', width: 46, height: 46, borderRadius: 14, background: chipBg ?? 'rgba(255,255,255,0.62)', color: chipInk ?? ink, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: primary ? 'inset 0 1px 0 rgba(255,255,255,0.55), 0 3px 8px rgba(160,120,20,0.4)' : CHIP_SH }}>
        <Icon name={icon} size={20} />
      </span>
      <span style={{ position: 'relative', flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontFamily: F.title, fontSize: SIZE.h2, fontWeight: W.medium, lineHeight: 1.15, letterSpacing: '-0.3px' }}>{title}</span>
        {sub && <span style={{ display: 'block', fontFamily: F.ui, fontSize: SIZE.caption, opacity: 0.74, marginTop: 2, lineHeight: 1.35 }}>{sub}</span>}
      </span>
    </button>
  );
}

// One "Waiting on you" card: a flagged action (check-in, reminder, CARE) the app is
// holding. Same card treatment as the old single notification, now rendered as a list.
// onDismiss is OPTIONAL: an item whose completion is verified elsewhere (the research
// check-in) has no ×, because a local "hide it" would silently contradict the real
// due-state and lose a research wave.
export function WaitingCard({ icon, title, sub, onTap, onDismiss, locked }: {
  icon: IconName; title: string; sub: string; onTap: () => void; onDismiss?: () => void; locked?: boolean;
}) {
  return (
    <div style={{ ...(locked ? LOCKED_LOOK : null), display: 'flex', alignItems: 'center', gap: SP.md, background: 'linear-gradient(180deg,#ffffff,#fdfbf7)', borderRadius: 18, padding: '13px 13px 13px 14px', boxShadow: `${SH}, ${HI}` }}>
      <span style={{ width: 38, height: 38, borderRadius: 12, background: 'rgba(46,138,134,0.12)', color: C.brandDark, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: CHIP_SH }}><Icon name={icon} size={18} /></span>
      <button onClick={onTap} aria-label={locked ? `${title}, locked, for members` : undefined} style={{ flex: 1, textAlign: 'left', border: 'none', background: 'none', cursor: 'pointer', padding: 0 }}>
        <span style={{ display: 'block', fontFamily: F.title, fontSize: SIZE.h3, fontWeight: W.medium, color: C.text }}>{title}</span>
        <span style={{ display: 'block', fontFamily: F.ui, fontSize: SIZE.caption, color: C.textMuted, marginTop: 1 }}>{sub}</span>
      </button>
      {locked && <span aria-hidden data-lock-icon style={{ display: 'flex', color: C.textFaint, flexShrink: 0 }}><Icon name="lock" size={15} sw={2} /></span>}
      {onDismiss && (
        <button aria-label="Dismiss" onClick={onDismiss} style={{ border: 'none', background: 'none', cursor: 'pointer', color: C.textFaint, fontSize: 18, lineHeight: 1, padding: '4px 6px', flexShrink: 0 }}>×</button>
      )}
    </div>
  );
}

export function ArrivalHeader(props: ArrivalHeaderProps) {
  const firstName = useAuth().session?.user.firstName ?? null;
  const [arrived, setArrived] = React.useState<ArrivalId | null>(() => sessionArrival);
  // Rebuild mode: latched when the user chooses "Continue your programme" (see enterRebuild).
  const [rebuild, setRebuild] = React.useState<boolean>(() => sessionRebuild);
  const [, setNonce] = React.useState(0);
  const bump = React.useCallback(() => setNonce((n) => n + 1), []);
  // Kept in a ref so the "arrived changed" effect below fires ONLY when the arrival
  // actually changes — not on every parent re-render (an inline callback identity
  // change was re-firing it and instantly closing the Calm panel).
  const onArrivedChangeRef = React.useRef(props.onArrivedChange);
  onArrivedChangeRef.current = props.onArrivedChange;

  React.useEffect(() => {
    // Re-ask "how are you arriving?" only on a NEW DAY (or a real app closure — a cold
    // start clears the module vars on its own). A quick app-switch / resume on the SAME
    // day keeps the chosen state, so it never flips on every foreground.
    //
    // This is intentionally idempotent: it ONLY clears when the calendar day has actually
    // rolled over. Same-day it returns early (and setArrived(null) when already null is a
    // React no-op), so we can safely re-check on many signals without ever nagging — the
    // signals decide WHEN we look, the day-key decides IF we reset.
    const maybeReset = () => {
      const today = arrivalDayKey();
      // Keep whichever mode was chosen today (an arrival OR rebuild); only a real day
      // rollover re-asks "how are you arriving?".
      if ((sessionArrival && sessionArrivalDay === today) || (sessionRebuild && sessionRebuildDay === today)) return;
      sessionArrival = null; sessionArrivalDay = null; saveArrival(null, null); setArrived(null);
      sessionRebuild = false; sessionRebuildDay = null; saveRebuild(false); setRebuild(false);
    };
    // Web has no real "cold start": a tab can stay open for days. visibilitychange catches
    // tab/app switches; focus catches window-level returns; pageshow catches bfcache restore
    // (back/forward navigation) — the web equivalent of native resume. The interval is the
    // only thing that catches midnight rolling over while the tab is open and focused.
    const onVis = () => { if (document.visibilityState === 'visible') maybeReset(); };
    const onShow = () => maybeReset();
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('focus', onShow);
    window.addEventListener('pageshow', onShow);
    const tick = window.setInterval(maybeReset, 60_000);
    let handle: { remove: () => void } | undefined;
    CapApp.addListener('resume', maybeReset).then((h) => { handle = h; }).catch(() => {});
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('focus', onShow);
      window.removeEventListener('pageshow', onShow);
      window.clearInterval(tick);
      handle?.remove();
    };
  }, []);

  const base = arrived && arrived !== 'unsafe' ? VARIANTS[arrived] : null;
  // Overlay the "returning" copy once the user has real progress (programme variant
  // warms from a neutral first-use invite to "pick up where you left off").
  let v = base && props.hasStarted && base.returning ? { ...base, ...base.returning } : base;
  if (v && TODAY_ONE_JOB) {
    // One job: the matched box routes to one next thing and never duplicates what the
    // action rows below already carry. Strip any programme CTA from it ("Continue" /
    // "Open the programme" — the "Continue your programme" row is THE programme CTA),
    // and collapse a single surviving choice into the plain CTA presentation.
    const keep = (a: ActionKey) => a !== 'continue';
    const choices = v.choices?.filter((c) => keep(c.action));
    const sequence = v.sequence?.filter((s) => keep(s.action));
    v = {
      ...v,
      cta: v.cta && keep(v.cta.action) ? v.cta : (choices?.length === 1 ? choices[0] : undefined),
      choices: choices && choices.length > 1 ? choices : undefined,
      sequence: sequence && sequence.length ? sequence : undefined,
    };
    // The settle action now starts the default 2-minute settle directly (the arrival
    // answered the "how are you feeling?" question), so the copy must promise that —
    // not a naming-and-choosing flow that no longer happens. Updated 2026-09-22 to
    // also name CARE, now that it's a second button here (not just a settle action).
    if (arrived === 'triggered') {
      v = { ...v, meta: 'Your arrival tells us enough. A two-minute settling breath, ready when you are — or go straight to CARE if you\'d rather process what just happened.' };
    }
  }
  // Notify the host ONLY when an arrival becomes active/inactive — not on every render.
  // For the 'working' (work-on-myself) variant `v` is a FRESH object each render (it
  // spreads `returning`), so depending on `v` re-fired this effect on every render and
  // continuously called onArrivedChange → the host's setCalmOpen(false) instantly
  // undid the Calm button. Depend on the stable boolean instead.
  const hasArrival = !!v;
  React.useEffect(() => { onArrivedChangeRef.current?.(hasArrival); }, [hasArrival]);

  const run = (a: ActionKey) => {
    switch (a) {
      case 'crisis':   return props.onOpenCrisis();
      case 'care':     return props.onOpenCare();
      // TODAY_ONE_JOB: run() serves ONLY the matched box, where an arrival state has
      // already been chosen — so the state pre-answers the Calm question and settling
      // starts directly (no second "how are you feeling?" triage). The generic "Calm
      // your body" action row calls props.onSettle itself and keeps the full chooser.
      case 'calm':     return TODAY_ONE_JOB && props.onSettleDirect ? props.onSettleDirect() : props.onSettle();
      case 'relax':    return props.onRelax();
      case 'hold':     return props.onHold();
      case 'continue':   return props.onContinue();
      case 'compassion': return props.onSelfCompassion();
      case 'toolkit':    return props.onOpenToolkit();
      case 'library':    return props.onOpenLibrary();
    }
  };

  const pick = (id: ArrivalId) => { if (id === 'unsafe') return props.onOpenCrisis(); sessionArrival = id; sessionArrivalDay = arrivalDayKey(); saveArrival(sessionArrival, sessionArrivalDay); setArrived(id); props.onArrivalPicked?.(); };

  // Enter rebuild mode: latch it for the session (so leaving a lesson returns HERE, not
  // to the arrival question) AND run the host's continue action (reveal feed + open the
  // current lesson). Used by every "Continue your programme" affordance.
  const enterRebuild = () => {
    sessionRebuild = true; sessionRebuildDay = arrivalDayKey(); saveRebuild(true); setRebuild(true);
    props.onContinueProgramme();
  };
  // "Change what state I'm in": drop BOTH the chosen arrival and rebuild mode so the
  // arrival question returns, and tell the host to hide the programme feed again.
  const resetState = () => {
    sessionArrival = null; sessionArrivalDay = null; saveArrival(null, null); setArrived(null);
    sessionRebuild = false; sessionRebuildDay = null; saveRebuild(false); setRebuild(false);
    props.onArrivalPicked?.();
  };

  // "Waiting on you" — the flagged actions the app is holding for the user. Rendered
  // ONLY AFTER the triage question has been answered: the phase-2 (arrival-matched)
  // front page and the rebuild home. The triage page itself stays a clean, fixed set
  // of arrival tiles and never gains extra options, so nothing competes with the one
  // question we are asking. A due check-in therefore waits for that first tap.
  const waitingOnYou = (() => {
    const w: Array<{ key: string; icon: IconName; title: string; sub: string; onTap: () => void; onDismiss?: () => void }> = [];
    // The check-in has NO dismiss and is NOT hidden on tap. Its visibility is driven
    // solely by props.checkinDue, which comes from the backend-verified count of
    // SUBMITTED check-ins — so opening the self-assessment and abandoning it half way
    // leaves the card exactly where it was. Only finishing it clears the card. (It used
    // to dismissForToday() on tap as well as on ×, which meant an abandoned check-in
    // vanished for the rest of the day and that research wave could be lost.) Users who
    // genuinely want it out of the way have the "not now" snooze inside the check-in
    // itself, which sets checkinSnoozeUntil and defers the whole prompt properly.
    if (props.checkinDue)
      w.push({ key: 'checkin', icon: 'heart', title: 'Complete a check-in', sub: 'A short, private wellbeing check-in', onTap: props.onStartCheckin });
    if (props.careDue)
      w.push({ key: 'care', icon: 'sparkle', title: 'Complete the CARE steps', sub: 'Critical task to retrain your triggered trauma responses', onTap: props.onOpenCare, onDismiss: props.onDismissCare });
    // First-time invitation into the relaxation breathing (the time picker, so they
    // choose their own length). Only while they have never had a session — once one
    // is started the host stops passing breathingNew and the card is gone for good.
    if (props.breathingNew && !dismissedToday('trybreathing'))
      w.push({ key: 'trybreathing', icon: 'moon', title: 'Try a short breathing exercise', sub: 'Choose how long, then breathe with us', onTap: () => { props.onRelax(); dismissForToday('trybreathing'); bump(); }, onDismiss: () => { dismissForToday('trybreathing'); bump(); } });
    if (!props.remindersSet && !dismissedToday('reminders') && Capacitor.isNativePlatform())
      w.push({ key: 'reminders', icon: 'clock', title: 'Set up a daily reminder', sub: 'A gentle nudge to return each day', onTap: () => { props.onSetupReminders(); dismissForToday('reminders'); bump(); }, onDismiss: () => { dismissForToday('reminders'); bump(); } });
    // Deferred post-lesson asks the host is holding (TODAY_ONE_JOB: the act survey +
    // chapter feedback moved here from the completion chain — they wait their turn in
    // the same rotation instead of interrupting the end of a lesson).
    if (TODAY_ONE_JOB && props.waitingExtras) {
      for (const it of props.waitingExtras) if (!dismissedToday(it.key)) w.push(it);
    }
    if (w.length === 0) return null;
    if (TODAY_ONE_JOB) {
      // One card at a time, rotated by calendar day. Nothing stacks, nothing counts up,
      // nothing escalates: a card the user never taps simply takes its turn again a few
      // days later. (Deterministic: day-number modulo the eligible list, which is built
      // in a stable order above.) The heading matches the app's own promise — these are
      // invitations, not obligations.
      const one = w[Math.floor(Date.now() / 86_400_000) % w.length];
      return (
        <>
          <div style={{ ...eyebrow, color: C.textMuted, margin: '22px 0 11px' }}>When you’re ready</div>
          <WaitingCard key={one.key} icon={one.icon} title={one.title} sub={one.sub} onTap={one.onTap} onDismiss={one.onDismiss} />
        </>
      );
    }
    return (
      <>
        <div style={{ ...eyebrow, color: C.textMuted, margin: '22px 0 11px' }}>Waiting on you</div>
        <div style={{ display: 'grid', gap: SP.sm }}>
          {w.map((it) => (
            <WaitingCard key={it.key} icon={it.icon} title={it.title} sub={it.sub} onTap={it.onTap} onDismiss={it.onDismiss} />
          ))}
        </div>
      </>
    );
  })();

  // The three universal actions + the "Waiting on you" list — shared by the phase-2
  // (arrival-matched) front page AND the rebuild home, so they stay identical.
  const focusedActions = (
    <>
      <div style={{ display: 'grid', gap: 13, marginTop: SP.lg }}>
        <ActionRow primary bg="linear-gradient(158deg,#f4e1bd,#ead0a0)" ink="#5a4218" chipBg="linear-gradient(150deg,#f0cd6f,#d9a52f)" chipInk="#fff" motif="horizon" image="/newbuttonimages/today_continueprogramme.png" imageOpacity={0.4} title="Continue your programme" sub={props.continueSub} icon="play" onClick={enterRebuild} />
        <ActionRow bg="linear-gradient(158deg,#d9e7e8,#c6dadd)" ink="#294a52" chipInk="#2c6a72" motif="journey" image="/newbuttonimages/today_calm-body-button-overlay.webp" imageOpacity={0.4} title="Calm your body" icon="heart" onClick={props.onSettle} />
        <ActionRow bg="linear-gradient(158deg,#dfe8d9,#cfdfc9)" ink="#35503f" chipInk="#3d6b4c" motif="paths" image="/newbuttonimages/today_toolkit-button-overlay-transparent.webp" title="Your toolkit" icon="sparkle" onClick={props.onOpenToolkit} />
      </div>

      {/* Ask ReNurture: a small button under the actions (replaces the old floating pill,
          which the new layout pushed off-screen). Carries a Beta mark, because it is the
          one part of the app still finding its feet and users should know that before
          they lean on it. */}
      {props.showAsk && props.onOpenAsk && (
        <button
          onClick={props.onOpenAsk}
          style={{ margin: '12px auto 0', width: 'fit-content', display: 'flex', alignItems: 'center', gap: 7, border: `1px solid ${C.border}`, background: C.surface, borderRadius: R.pill, cursor: 'pointer', color: C.brandDark, fontFamily: F.ui, fontSize: SIZE.md, fontWeight: W.semibold, padding: '8px 16px', boxShadow: CHIP_SH }}
        >
          <Icon name="sparkle" size={15} /> Ask ReNurture
          <span style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#9b6712', background: 'rgba(200,134,26,0.16)', borderRadius: 999, padding: '2px 7px' }}>Beta</span>
        </button>
      )}
      {waitingOnYou}
    </>
  );

  // ── REBUILD HOME — chosen "Continue your programme": stay in the programme view on
  //    return from a lesson instead of re-asking the arrival question (focused only). ──
  if (!v && rebuild && FOCUSED_ARRIVAL) {
    return (
      <div style={{ ...band, paddingTop: 24, paddingBottom: 28, animation: 'arriveIn 460ms cubic-bezier(0.22,0.61,0.36,1) both' }}>
        <div style={{ marginBottom: 4 }}>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 2 }}>
            <button onClick={resetState} style={{ border: 'none', background: 'none', cursor: 'pointer', ...eyebrow, color: C.brandDark }}>↻ Change my state</button>
          </div>
          <h1 style={{ fontFamily: F.title, fontSize: 29, fontWeight: W.medium, letterSpacing: '-0.4px', lineHeight: 1.15, color: C.text, margin: '2px 0 0' }}>{greeting(firstName)}</h1>
        </div>
        {focusedActions}
      </div>
    );
  }

  // ── PHASE 1 — the arrival question (clean: no notifications) ──
  if (!v) {
    const unsafe = ARRIVALS.find((a) => a.id === 'unsafe')!;
    const rest = ARRIVALS.filter((a) => a.id !== 'unsafe');
    return (
      <div style={{ ...band, paddingTop: 20, paddingBottom: 34 }}>
        {props.programmeCta && <ProgrammeCtaCard cta={props.programmeCta} />}
        <h1 style={{ fontFamily: F.title, fontSize: SIZE.h1, fontWeight: W.regular, lineHeight: 1.12, letterSpacing: '-0.5px', color: C.text, margin: '4px 0 0' }}>
          How are you <em style={{ fontStyle: 'italic', fontWeight: W.medium }}>arriving today?</em>
        </h1>
        <p style={{ fontFamily: F.ui, fontSize: SIZE.md, color: C.textMuted, lineHeight: 1.55, margin: '12px 0 0' }}>
          Tap what fits and we’ll meet you there. You’ll see this question every time you open the app.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 26 }}>
          <Tile tone={unsafe.tone} ink={unsafe.ink} title={unsafe.label} sub={unsafe.sub} motif={unsafe.motif} image={ARRIVAL_IMG[unsafe.id]} imagePosition="center bottom" full big onClick={() => pick(unsafe.id)} />
          {rest.map((a) =>
            // "Here to work on myself" is now a direct route INTO the programme rather
            // than another arrival state: it enters rebuild mode and opens the last
            // programme (main / self-compassion) at its next lesson.
            a.id === 'working' ? (
              <Tile key={a.id} tone="#f1d9b4" ink="#5a4218" title="Continue with programme" sub="Pick up your next lesson" motif="horizon" icon="play" image="/newbuttonimages/today_continueprogramme.png" imageOpacity={0.4} big primary onClick={enterRebuild} />
            ) : (
              <Tile key={a.id} tone={a.tone} ink={a.ink} title={a.label} sub={a.sub} motif={a.motif} image={ARRIVAL_IMG[a.id]} big onClick={() => pick(a.id)} />
            ),
          )}
        </div>

        {/* Always-available full-width shortcut: skip the question and settle the body
            first. Runs a fixed 2-minute breathing reset, then returns to this screen —
            it does NOT choose an arrival state, so the question is still here on exit. */}
        {(
          <button
            onClick={props.onSettleNow}
            style={{
              position: 'relative', overflow: 'hidden', width: '100%', marginTop: 14,
              display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4,
              textAlign: 'left', border: 'none', cursor: 'pointer',
              background: 'linear-gradient(158deg,#35a39d,#256f6c)', color: '#fff', borderRadius: 20, padding: '16px 18px',
              boxShadow: '0 2px 4px rgba(20,60,58,0.12), 0 16px 34px -16px rgba(20,60,58,0.5), inset 0 1px 0 rgba(255,255,255,0.25)',
            }}
          >
            <img src="/newbuttonimages/today_calm-your-body-first-overlay.webp" alt="" aria-hidden decoding="async" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', opacity: 0.4, pointerEvents: 'none', WebkitMaskImage: 'linear-gradient(to right, transparent 0%, rgba(0,0,0,0.4) 40%, #000 78%)', maskImage: 'linear-gradient(to right, transparent 0%, rgba(0,0,0,0.4) 40%, #000 78%)' }} />
            <span style={{ position: 'relative', fontFamily: F.title, fontSize: SIZE.h3, fontWeight: W.medium, lineHeight: 1.15 }}>Settle me</span>
            <span style={{ position: 'relative', fontFamily: F.ui, fontSize: SIZE.caption, opacity: 0.85 }}>Start a 2-minute breathing exercise</span>
          </button>
        )}

        {/* No "Waiting on you" here by design: the triage page asks one question and
            offers a fixed set of arrival tiles (plus Settle me). Flagged actions appear
            on the next screen, once the user has told us how they are arriving. */}
      </div>
    );
  }

  // ── PHASE 2 — reconstructed header + notification slot + menu ──
  // The matched header box now takes the COLOUR of the arrival tile the user tapped
  // (v.tone), so it feels like a warm continuation of their choice rather than a cold
  // white card. Its buttons are frosted (semi-transparent white) with the arrival's
  // ink for text, so they lift off the coloured box without reintroducing hard white.
  const primaryBtn: React.CSSProperties = { background: 'linear-gradient(180deg,#ffffff,#f3f6f6)', color: v.ink, border: 'none', borderRadius: R.md, cursor: 'pointer', fontFamily: F.ui, fontWeight: W.semibold, boxShadow: '0 3px 10px rgba(30,50,60,0.15), inset 0 1px 0 rgba(255,255,255,0.7)' };
  const softBtn: React.CSSProperties = { background: 'rgba(255,255,255,0.5)', color: v.ink, border: 'none', borderRadius: R.md, cursor: 'pointer', fontFamily: F.ui, fontWeight: W.semibold, boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.6), 0 1px 3px rgba(30,50,60,0.1)' };
  return (
    <div style={{ ...band, paddingTop: 24, paddingBottom: 28, animation: 'arriveIn 460ms cubic-bezier(0.22,0.61,0.36,1) both' }}>
      {props.programmeCta && <ProgrammeCtaCard cta={props.programmeCta} />}
      {/* Greeting — given room to breathe now the feed no longer crowds beneath it. */}
      <div style={{ marginBottom: 4 }}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 2 }}>
          <button onClick={resetState} style={{ border: 'none', background: 'none', cursor: 'pointer', ...eyebrow, color: C.brandDark }}>↻ Change my state</button>
        </div>
        <h1 style={{ fontFamily: F.title, fontSize: 29, fontWeight: W.medium, letterSpacing: '-0.4px', lineHeight: 1.15, color: C.text, margin: '2px 0 0' }}>{greeting(firstName)}</h1>
      </div>

      <section style={{ position: 'relative', overflow: 'hidden', background: `linear-gradient(158deg, rgba(255,255,255,0.42), rgba(255,255,255,0) 60%), ${v.tone}`, borderRadius: 22, padding: '20px 20px 18px', marginTop: SP.lg, boxShadow: `${SH_LIFT}, ${HI}` }}>
        {arrived && ARRIVAL_IMG[arrived] ? (
          // Same hand-painted overlay as the arrival's triage tile, faded on the left so
          // the box's headline + copy + buttons stay legible over it.
          <img src={ARRIVAL_IMG[arrived]} alt="" aria-hidden decoding="async" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', opacity: 0.4, pointerEvents: 'none', WebkitMaskImage: 'linear-gradient(to right, transparent 0%, rgba(0,0,0,0.4) 42%, #000 80%)', maskImage: 'linear-gradient(to right, transparent 0%, rgba(0,0,0,0.4) 42%, #000 80%)' }} />
        ) : (
          <span aria-hidden style={{ position: 'absolute', right: -26, top: -30, color: v.ink, opacity: 0.1, pointerEvents: 'none', lineHeight: 0 }}>
            <Motif kind={ARRIVALS.find((a) => a.id === arrived)?.motif ?? 'disturb'} color={v.ink} size={168} />
          </span>
        )}
        <div style={{ position: 'relative' }}>
          <div style={{ ...eyebrow, color: v.ink, opacity: 0.9 }}>For how you’ve arrived</div>
          <h2 style={{ fontFamily: F.title, fontSize: SIZE.h2, fontWeight: W.medium, lineHeight: 1.15, letterSpacing: '-0.3px', color: C.text, margin: '5px 0 0' }}>
            {v.headline} <em style={{ fontStyle: 'italic', color: v.ink }}>{v.headlineEm}</em>
          </h2>
          <p style={{ fontFamily: F.ui, fontSize: SIZE.body, lineHeight: 1.5, color: C.textBody, margin: '8px 0 14px' }}>{v.meta}</p>

          {v.cta && (
            <button onClick={() => run(v.cta!.action)} style={{ ...primaryBtn, padding: '11px 16px', fontSize: SIZE.md }}>{v.cta.label} →</button>
          )}
          {v.sequence && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: SP.sm }}>
              {v.sequence.map((s, i) => (
                <button key={s.action} onClick={() => run(s.action)} style={{ ...(s.primary ? primaryBtn : softBtn), textAlign: 'left', padding: '10px 12px', display: 'flex', alignItems: 'center', gap: SP.sm }}>
                  <span style={{ width: 21, height: 21, borderRadius: R.pill, border: `1px solid ${v.ink}`, color: v.ink, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: W.semibold, flexShrink: 0 }}>{i + 1}</span>
                  <span style={{ fontSize: SIZE.md, fontWeight: W.semibold }}>{s.name}</span>
                </button>
              ))}
            </div>
          )}
          {v.choices && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: SP.sm }}>
              {v.choices.map((c) => (
                <button key={c.action} onClick={() => run(c.action)} style={{ ...softBtn, padding: '12px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: SIZE.md, fontWeight: W.bold }}>{c.label}</span><span aria-hidden>→</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      {FOCUSED_ARRIVAL ? (
        focusedActions
      ) : (
        <>
          <div style={{ marginTop: SP.md }}><NotificationCard {...props} bump={bump} /></div>
          {/* The legacy universal 4-tile menu, retained for the non-focused layout. */}
          <div style={{ ...eyebrow, color: C.textMuted, margin: '18px 0 11px' }}>Or anything else</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: SP.sm }}>
            {MENU.map((o) => (
              <Tile key={o.key} tone={o.wash} ink={o.ink} title={o.title} sub={o.sub} motif={o.motif} icon={o.icon} onClick={() => run(o.action)} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
